// Obsah vstupních (landing) stránek jednotlivých SEO nástrojů — programmatic SEO.
// Každý nástroj má vlastní URL /nastroje/{slug}, unikátní title/meta/H1, vysvětlení
// a FAQ (→ FAQPage JSON-LD). Čeština je základ; překlady doplníme dávkově později.

export type ToolInputType = 'url' | 'compare' | 'bulk'

export interface ToolPage {
  slug: string          // /nastroje/{slug}
  toolId: string        // endpoint /tools/{toolId} + ikona
  inputType: ToolInputType
  metaTitle: string     // <title>
  metaDesc: string      // meta description
  h1: string
  intro: string
  what: string[]        // „Co nástroj kontroluje"
  why: string           // „Proč na tom záleží"
  faq: { q: string; a: string }[]
  related: string[]     // slugy souvisejících nástrojů
  kb?: string           // id v znalostní bázi (/napoveda/{kb})
  keywords?: string     // meta keywords (Seznam.cz); když chybí, odvodí se z h1
}

export const TOOL_PAGES: ToolPage[] = [
  {
    slug: 'kontrola-robots-txt', toolId: 'robots', inputType: 'url',
    metaTitle: 'Kontrola robots.txt zdarma: test přístupu botů | SEO Tools',
    metaDesc: 'Online kontrola souboru robots.txt. Ověřte, zda umožňuje indexaci Googlem a Seznamem, zda neblokuje důležité stránky a obsahuje odkaz na sitemapu. Zdarma a bez registrace.',
    h1: 'Kontrola robots.txt',
    intro: 'Zadejte web a okamžitě zjistíte, jestli je váš robots.txt správně nastavený a nebrání vyhledávačům v indexaci důležitých stránek.',
    what: [
      'Existence a dostupnost souboru robots.txt',
      'Pravidla Allow / Disallow a které sekce blokují boty',
      'Přístup hlavních botů (Googlebot, Seznambot, Bingbot)',
      'Odkaz na XML sitemapu uvnitř robots.txt',
      'Časté chyby: omylem zablokovaný celý web nebo CSS/JS',
    ],
    why: 'Robots.txt je první soubor, který vyhledávač u webu načte. Jedno špatné pravidlo „Disallow: /" dokáže vyřadit celý web z indexu. Naopak chybějící odkaz na sitemapu zpomaluje objevování nových stránek. Správné nastavení šetří crawl budget a směruje boty tam, kam mají.',
    faq: [
      { q: 'Co je robots.txt?', a: 'Textový soubor v kořeni webu (např. example.com/robots.txt), který říká vyhledávacím robotům, které části webu smí procházet a které ne.' },
      { q: 'Může robots.txt zablokovat web z Googlu?', a: 'Ano. Pravidlo „Disallow: /" zakáže procházení celého webu. Stránky pak většinou z indexu vypadnou nebo se neaktualizují.' },
      { q: 'Musí robots.txt obsahovat sitemapu?', a: 'Není to povinné, ale doporučené. Řádek „Sitemap: https://example.com/sitemap.xml" pomáhá vyhledávačům rychleji najít všechny stránky.' },
    ],
    related: ['kontrola-sitemap', 'kontrola-presmerovani', 'validace-strukturovanych-dat'], kb: 'robots',
  },
  {
    slug: 'kontrola-sitemap', toolId: 'sitemap', inputType: 'url',
    metaTitle: 'Kontrola XML sitemap: validátor sitemapy zdarma | SEO Tools',
    metaDesc: 'Zkontrolujte XML sitemapu webu online. Ověříme dostupnost, formát, počet URL, datumy aktualizace a chyby. Zdarma, bez registrace.',
    h1: 'Kontrola XML sitemap',
    intro: 'Ověřte, že vaše XML sitemapa je dostupná, validní a obsahuje správné URL, aby vyhledávače našly a indexovaly všechny vaše stránky.',
    what: [
      'Dostupnost a správný formát sitemap.xml',
      'Počet URL a případné překročení limitů',
      'Platnost adres a jejich datumy poslední změny (lastmod)',
      'Sitemap index a vnořené sitemapy',
      'Soulad s odkazem v robots.txt',
    ],
    why: 'XML sitemapa je seznam stránek, které chcete mít v indexu. U větších webů zásadně urychluje objevování nového obsahu a pomáhá vyhledávačům pochopit strukturu webu. Chybná nebo zastaralá sitemapa znamená, že se nové stránky indexují pomalu nebo vůbec.',
    faq: [
      { q: 'K čemu je XML sitemapa?', a: 'Je to strojově čitelný seznam URL vašeho webu, který vyhledávačům pomáhá najít a procházet všechny důležité stránky.' },
      { q: 'Kolik URL může sitemapa obsahovat?', a: 'Jedna sitemapa max. 50 000 URL a 50 MB. Pro větší weby se používá sitemap index odkazující na více souborů.' },
      { q: 'Kam sitemapu nahrát?', a: 'Obvykle do kořene webu (example.com/sitemap.xml) a odkázat na ni v robots.txt i v Google Search Console.' },
    ],
    related: ['kontrola-robots-txt', 'kontrola-presmerovani', 'kontrola-mrtvych-odkazu'], kb: 'sitemap',
  },
  {
    slug: 'validace-strukturovanych-dat', toolId: 'schema', inputType: 'url',
    metaTitle: 'Validace strukturovaných dat: test Schema.org / JSON-LD | SEO Tools',
    metaDesc: 'Online test strukturovaných dat (Schema.org, JSON-LD). Zjistěte, jaké typy schémat web používá a zda jsou připravené pro rich snippets ve vyhledávání. Zdarma.',
    h1: 'Validace strukturovaných dat (Schema.org)',
    intro: 'Zkontrolujte strukturovaná data svého webu a zjistěte, zda jsou připravená pro rozšířené výsledky (rich snippets) v Googlu i AI vyhledávačích.',
    what: [
      'Detekce formátů JSON-LD, Microdata a RDFa',
      'Použité typy schémat (Article, Product, FAQ, Breadcrumb, Organization…)',
      'Chybějící povinné vlastnosti jednotlivých typů',
      'Připravenost pro rich snippets (hvězdičky, ceny, FAQ)',
      'Signály pro AI vyhledávače (GEO)',
    ],
    why: 'Strukturovaná data říkají vyhledávačům, co obsah znamená: recept, produkt, článek, FAQ. Díky tomu se ve výsledcích zobrazují atraktivnější výpisy (hvězdičky, ceny, otázky), které mají vyšší prokliky. Jsou také klíčové pro citace v AI nástrojích jako ChatGPT a Perplexity.',
    faq: [
      { q: 'Co jsou strukturovaná data?', a: 'Standardizovaný způsob (nejčastěji Schema.org v JSON-LD), jak v kódu stránky popsat její obsah, aby mu vyhledávače lépe rozuměly.' },
      { q: 'Zlepší strukturovaná data pozice?', a: 'Přímo nejsou faktorem pozice, ale umožňují rich snippets, které zvyšují prokliky (CTR), a pomáhají AI vyhledávačům obsah správně citovat.' },
      { q: 'Jaký formát použít?', a: 'Google doporučuje JSON-LD vkládaný do hlavičky stránky, protože je nejjednodušší na správu a nejméně náchylný k chybám.' },
    ],
    related: ['geo-ai-optimalizace', 'analyza-obsahu', 'nahled-serp'], kb: 'structured_data',
  },
  {
    slug: 'nahled-serp', toolId: 'serp', inputType: 'url',
    metaTitle: 'SERP Preview: náhled webu ve výsledcích vyhledávání | SEO Tools',
    metaDesc: 'Podívejte se, jak vaše stránka vypadá ve výsledcích Googlu a Seznamu. Kontrola délky title a meta description, ořezání a optimalizace pro vyšší prokliky. Zdarma.',
    h1: 'SERP Preview: náhled ve vyhledávání',
    intro: 'Zjistěte, jak se vaše stránka zobrazí ve výsledcích vyhledávání, a zda title a popisek nepřesahují optimální délku.',
    what: [
      'Vizuální náhled výpisu na desktopu i mobilu',
      'Délka title tagu a riziko ořezání',
      'Délka meta description a její využití',
      'Zobrazení URL / breadcrumb',
      'Doporučení pro vyšší míru prokliku (CTR)',
    ],
    why: 'Title a meta description jsou vaše „reklama" ve výsledcích vyhledávání. Pokud jsou příliš dlouhé, Google je ořízne; pokud jsou nevýrazné, lidé nekliknou. Optimalizovaný výpis zvyšuje míru prokliku bez nutnosti zlepšovat pozici.',
    faq: [
      { q: 'Jak dlouhý má být title?', a: 'Zhruba 50–60 znaků. Delší tituly Google ve výsledcích ořízne třemi tečkami.' },
      { q: 'Jak dlouhá má být meta description?', a: 'Zhruba 120–160 znaků. Slouží k nalákání na proklik; přímo neovlivňuje pozici, ale ovlivňuje CTR.' },
      { q: 'Používá Google vždy moji meta description?', a: 'Ne vždy. Někdy si vygeneruje vlastní úryvek z obsahu stránky podle dotazu uživatele.' },
    ],
    related: ['analyza-obsahu', 'validace-strukturovanych-dat', 'test-rychlosti-webu'], kb: 'meta_description',
  },
  {
    slug: 'kontrola-hreflang', toolId: 'hreflang', inputType: 'url',
    metaTitle: 'Kontrola hreflang: test vícejazyčných tagů zdarma | SEO Tools',
    metaDesc: 'Online test hreflang tagů pro vícejazyčné weby. Ověříme správnost jazykových verzí, vzájemné odkazy a x-default. Zdarma, bez registrace.',
    h1: 'Kontrola hreflang tagů',
    intro: 'Máte web ve více jazycích? Ověřte, že hreflang tagy správně směrují uživatele i vyhledávače na odpovídající jazykovou verzi.',
    what: [
      'Přítomnost a syntaxe hreflang tagů',
      'Platné kódy jazyků a zemí (např. cs, en-GB)',
      'Vzájemné (obousměrné) odkazování verzí',
      'Tag x-default pro výchozí verzi',
      'Časté chyby: chybějící zpětný odkaz, špatný kód',
    ],
    why: 'Hreflang říká Googlu, která jazyková verze stránky patří kterému publiku. Bez něj se ve výsledcích může zobrazovat špatná jazyková verze, verze si konkurují nebo se považují za duplicitní obsah. U mezinárodních webů je to zásadní.',
    faq: [
      { q: 'Co je hreflang?', a: 'Atribut, který vyhledávačům říká, pro jaký jazyk a region je daná verze stránky určená, aby uživatel viděl správnou jazykovou mutaci.' },
      { q: 'Musí být hreflang obousměrný?', a: 'Ano. Pokud stránka A odkazuje na verzi B, musí i B odkazovat zpět na A, jinak Google tagy ignoruje.' },
      { q: 'K čemu je x-default?', a: 'Označuje výchozí verzi pro uživatele, jejichž jazyk/region neodpovídá žádné konkrétní mutaci.' },
    ],
    related: ['kontrola-sitemap', 'analyza-obsahu', 'validace-strukturovanych-dat'], kb: 'language',
  },
  {
    slug: 'test-mobilni-optimalizace', toolId: 'mobile', inputType: 'url',
    metaTitle: 'Test mobilní optimalizace webu zdarma | SEO Tools',
    metaDesc: 'Zkontrolujte, zda je web optimalizovaný pro mobily: viewport, velikost písma, responzivita a použitelnost na dotyku. Zdarma a bez registrace.',
    h1: 'Test mobilní optimalizace',
    intro: 'Ověřte, jestli je váš web připravený na mobily. Google indexuje weby primárně podle jejich mobilní verze (mobile-first).',
    what: [
      'Nastavení viewport meta tagu',
      'Responzivní rozvržení a šířka obsahu',
      'Velikost písma a čitelnost na malých displejích',
      'Velikost a rozestup klikacích prvků',
      'Blokovaný zoom a další překážky použitelnosti',
    ],
    why: 'Google používá mobile-first indexing: hodnotí web podle jeho mobilní verze. Pokud je na mobilu nepoužitelný (malé písmo, vodorovné posouvání, drobná tlačítka), ztrácíte pozice i návštěvníky. Většina provozu dnes pochází z mobilů.',
    faq: [
      { q: 'Co je mobile-first indexing?', a: 'Google pro hodnocení a indexaci používá mobilní verzi webu. Co není vidět nebo nefunguje na mobilu, jako by neexistovalo.' },
      { q: 'Jak poznám, že web není mobilní?', a: 'Typicky chybí viewport tag, je nutné vodorovně posouvat, písmo je drobné a tlačítka se těžko trefují prstem.' },
      { q: 'Pomůže responzivní design?', a: 'Ano, responzivní design je doporučený přístup: jeden web se přizpůsobí všem velikostem obrazovek.' },
    ],
    related: ['test-rychlosti-webu', 'ux-audit', 'kontrola-pristupnosti'], kb: 'viewport',
  },
  {
    slug: 'kontrola-bezpecnostnich-hlavicek', toolId: 'security', inputType: 'url',
    metaTitle: 'Kontrola bezpečnostních hlaviček webu zdarma | SEO Tools',
    metaDesc: 'Online test HTTP bezpečnostních hlaviček: HSTS, CSP, X-Frame-Options a dalších. Zjistěte, jak je web chráněný. Zdarma, bez registrace.',
    h1: 'Kontrola bezpečnostních hlaviček',
    intro: 'Zkontrolujte HTTP bezpečnostní hlavičky svého webu a zjistěte, jak je chráněný před útoky typu clickjacking, XSS nebo downgrade na HTTP.',
    what: [
      'Strict-Transport-Security (HSTS): vynucení HTTPS',
      'Content-Security-Policy (CSP): ochrana proti XSS',
      'X-Frame-Options: ochrana proti clickjackingu',
      'X-Content-Type-Options, Referrer-Policy, Permissions-Policy',
      'Celkové bezpečnostní skóre a doporučení',
    ],
    why: 'Bezpečnostní hlavičky chrání návštěvníky i vaši reputaci. Google preferuje bezpečné weby a HTTPS je potvrzený faktor hodnocení. Chybějící hlavičky navíc usnadňují útoky, které mohou poškodit důvěru i SEO.',
    faq: [
      { q: 'Jsou bezpečnostní hlavičky faktorem SEO?', a: 'HTTPS ano. Ostatní hlavičky přímo pozice neovlivňují, ale chrání uživatele a důvěryhodnost webu, což se SEO dlouhodobě souvisí.' },
      { q: 'Co je HSTS?', a: 'Hlavička, která prohlížeči nařídí komunikovat s webem výhradně přes HTTPS, i kdyby uživatel napsal http://.' },
      { q: 'Co je CSP?', a: 'Content-Security-Policy omezuje, odkud se smí načítat skripty a obsah, čímž výrazně snižuje riziko XSS útoků.' },
    ],
    related: ['kontrola-dns-ssl', 'kontrola-presmerovani', 'kontrola-cookies-gdpr'], kb: 'https',
  },
  {
    slug: 'test-rychlosti-webu', toolId: 'pagespeed', inputType: 'url',
    metaTitle: 'Test rychlosti webu zdarma: PageSpeed a Core Web Vitals | SEO Tools',
    metaDesc: 'Změřte rychlost načítání webu a Core Web Vitals (LCP, CLS, INP). Zjistěte, co web zpomaluje a jak ho zrychlit. Zdarma, bez registrace.',
    h1: 'Test rychlosti webu (PageSpeed)',
    intro: 'Změřte výkon a rychlost načítání svého webu včetně Core Web Vitals a získejte konkrétní tipy, co zrychlit.',
    what: [
      'Core Web Vitals: LCP, CLS, INP',
      'Doba načtení a velikost stránky',
      'Příležitosti ke zrychlení (obrázky, JS, CSS)',
      'Blokující skripty a nevyužité zdroje',
      'Výkonnostní skóre s doporučeními',
    ],
    why: 'Rychlost je potvrzený faktor hodnocení (Core Web Vitals) a zároveň zásadně ovlivňuje konverze: každá vteřina navíc zvyšuje odchodovost. Pomalý web ztrácí pozice i zákazníky, zvlášť na mobilech.',
    faq: [
      { q: 'Co jsou Core Web Vitals?', a: 'Sada metrik Googlu měřících uživatelský zážitek: LCP (rychlost vykreslení), CLS (vizuální stabilita) a INP (odezva na interakci).' },
      { q: 'Je rychlost faktorem SEO?', a: 'Ano. Core Web Vitals jsou součástí hodnocení page experience, zvlášť při srovnatelně kvalitním obsahu.' },
      { q: 'Co web nejčastěji zpomaluje?', a: 'Velké neoptimalizované obrázky, příliš mnoho JavaScriptu, pomalý hosting a chybějící cache.' },
    ],
    related: ['test-mobilni-optimalizace', 'detekce-technologii', 'analyza-obsahu'], kb: 'performance',
  },
  {
    slug: 'kontrola-presmerovani', toolId: 'redirects', inputType: 'url',
    metaTitle: 'Kontrola přesměrování webu zdarma: 301, 302, HTTP→HTTPS | SEO Tools',
    metaDesc: 'Otestujte řetězce přesměrování (301/302), přechod HTTP→HTTPS a www/non-www. Odhalte zbytečné skoky a smyčky. Zdarma, bez registrace.',
    h1: 'Kontrola přesměrování (301/302)',
    intro: 'Zkontrolujte, jak váš web přesměrovává, zda správně sjednocuje HTTP/HTTPS a www, a zda neobsahuje zbytečné řetězce nebo smyčky.',
    what: [
      'Typ přesměrování (301 trvalé vs 302 dočasné)',
      'Řetězce přesměrování a počet skoků',
      'Sjednocení HTTP → HTTPS',
      'Sjednocení www a non-www verze',
      'Detekce smyček a slepých konců',
    ],
    why: 'Špatná přesměrování ředí SEO sílu a zpomalují web. Každý skok navíc znamená pomalejší načtení a ztrátu části „link juice". Dočasné 302 tam, kde má být trvalé 301, brání předání hodnoty na novou adresu.',
    faq: [
      { q: 'Jaký je rozdíl mezi 301 a 302?', a: '301 je trvalé přesměrování (předává SEO hodnotu na novou URL), 302 je dočasné (hodnotu předává jen omezeně). Pro trvalé změny používejte 301.' },
      { q: 'Vadí řetězce přesměrování?', a: 'Ano. Přesměrování A→B→C zpomaluje načítání a oslabuje předanou hodnotu. Ideální je jediný skok přímo na cíl.' },
      { q: 'Mám sjednotit www a non-www?', a: 'Ano. Zvolte jednu hlavní variantu a druhou na ni trvale (301) přesměrujte, aby nevznikal duplicitní obsah.' },
    ],
    related: ['kontrola-robots-txt', 'kontrola-bezpecnostnich-hlavicek', 'kontrola-mrtvych-odkazu'], kb: 'canonical',
  },
  {
    slug: 'analyza-obsahu', toolId: 'content', inputType: 'url',
    metaTitle: 'Analýza obsahu stránky: počet slov, čitelnost, klíčová slova | SEO Tools',
    metaDesc: 'Rozbor obsahu webové stránky: počet slov, čitelnost, hustota klíčových slov, nadpisy a struktura. Zjistěte, jak obsah zlepšit. Zdarma.',
    h1: 'Analýza obsahu stránky',
    intro: 'Rozeberte obsah své stránky (počet slov, čitelnost, strukturu nadpisů a klíčová slova) a zjistěte, kde má rezervy.',
    what: [
      'Počet slov a celkový rozsah obsahu',
      'Struktura nadpisů H1–H6',
      'Čitelnost textu',
      'Nejčastější slova a klíčové fráze',
      'Poměr textu k HTML a tenký obsah',
    ],
    why: 'Kvalitní a dostatečně obsáhlý obsah je základ SEO. Vyhledávače upřednostňují stránky, které téma pokrývají do hloubky a mají logickou strukturu nadpisů. Tenký nebo špatně strukturovaný obsah se hůř umisťuje a hůř cituje v AI vyhledávačích.',
    faq: [
      { q: 'Kolik slov má mít stránka?', a: 'Univerzální číslo neexistuje, záleží na záměru dotazu. Obsah by ale měl téma pokrýt lépe než konkurence na prvních pozicích.' },
      { q: 'Vadí příliš málo textu?', a: 'Tenký obsah (pár vět) se umisťuje obtížně. Stránka by měla nabídnout reálnou hodnotu a odpovědět na dotazy uživatele.' },
      { q: 'Co je hustota klíčových slov?', a: 'Jak často se klíčová fráze v textu opakuje. Důležitější než hustota je ale přirozenost a tematická šíře obsahu.' },
    ],
    related: ['nahled-serp', 'geo-ai-optimalizace', 'validace-strukturovanych-dat'], kb: 'content_quality',
  },
  {
    slug: 'detekce-technologii', toolId: 'technology', inputType: 'url',
    metaTitle: 'Detekce technologií webu zdarma: CMS, framework, analytika | SEO Tools',
    metaDesc: 'Zjistěte, na čem web běží: CMS (WordPress, Shoptet…), frameworky, analytické a marketingové nástroje, CDN a hosting. Zdarma, bez registrace.',
    h1: 'Detekce technologií webu',
    intro: 'Zjistěte, jaké technologie web používá, od CMS a frameworku přes analytiku až po CDN a marketingové nástroje.',
    what: [
      'CMS a e-shop platforma (WordPress, Shoptet, Shopify…)',
      'Frontendové frameworky a knihovny',
      'Analytické a marketingové nástroje',
      'CDN, hosting a serverové technologie',
      'Písma, widgety a další integrace',
    ],
    why: 'Znalost technologického zázemí pomáhá při SEO auditu i konkurenční analýze. Prozradí, jak je web postavený, kde mohou být výkonnostní úzká místa a jaké nástroje konkurence používá pro měření a marketing.',
    faq: [
      { q: 'Jak nástroj pozná použité technologie?', a: 'Z HTML kódu, hlaviček odpovědi, názvů souborů a charakteristických stop, které jednotlivé technologie zanechávají.' },
      { q: 'Najde i analytiku konkurence?', a: 'Ano, obvykle odhalí běžné nástroje jako Google Analytics, Tag Manager, reklamní pixely a podobně.' },
      { q: 'Souvisí použité technologie se SEO?', a: 'Nepřímo: ovlivňují rychlost, indexovatelnost a možnosti optimalizace. Některé platformy mají SEO snazší než jiné.' },
    ],
    related: ['test-rychlosti-webu', 'kontrola-bezpecnostnich-hlavicek', 'kontrola-dns-ssl'], kb: 'technology',
  },
  {
    slug: 'kontrola-pristupnosti', toolId: 'accessibility', inputType: 'url',
    metaTitle: 'Kontrola přístupnosti webu zdarma: WCAG audit | SEO Tools',
    metaDesc: 'Online kontrola přístupnosti webu podle WCAG: alt texty, kontrast, ARIA, struktura nadpisů a ovládání klávesnicí. Zdarma, bez registrace.',
    h1: 'Kontrola přístupnosti (WCAG)',
    intro: 'Ověřte přístupnost svého webu podle zásad WCAG, aby ho mohli pohodlně používat všichni návštěvníci, včetně lidí s postižením.',
    what: [
      'Alternativní texty u obrázků (alt)',
      'Barevný kontrast textu a pozadí',
      'Struktura nadpisů a orientační body',
      'ARIA atributy a popisky formulářů',
      'Ovladatelnost klávesnicí',
    ],
    why: 'Přístupný web je povinnost (zvlášť pro veřejný sektor a podle směrnice EU), ale i SEO výhoda. Mnoho prvků přístupnosti (alt texty, sémantická struktura, popisky) pomáhá zároveň vyhledávačům obsahu lépe rozumět.',
    faq: [
      { q: 'Co je WCAG?', a: 'Web Content Accessibility Guidelines, tedy mezinárodní standard přístupnosti webu. Obvykle se cílí na úroveň AA.' },
      { q: 'Souvisí přístupnost se SEO?', a: 'Ano, nepřímo. Alt texty, sémantické HTML a logická struktura nadpisů pomáhají přístupnosti i porozumění obsahu vyhledávači.' },
      { q: 'Je přístupnost povinná?', a: 'Pro řadu subjektů ano: v EU platí směrnice o přístupnosti, která se postupně rozšiřuje i na komerční sektor.' },
    ],
    related: ['ux-audit', 'test-mobilni-optimalizace', 'analyza-obsahu'], kb: 'accessibility',
  },
  {
    slug: 'ux-audit', toolId: 'ux', inputType: 'url',
    metaTitle: 'UX audit webu zdarma: použitelnost a čitelnost | SEO Tools',
    metaDesc: 'Rychlý UX audit webu: velikost písma, kontrast, čitelnost odkazů, blokovaný zoom a responzivita. Zlepšete použitelnost a konverze. Zdarma.',
    h1: 'UX / UI audit webu',
    intro: 'Získejte rychlý pohled na použitelnost svého webu a na to, co může odrazovat návštěvníky a snižovat konverze.',
    what: [
      'Velikost a čitelnost písma',
      'Barevný kontrast a vizuální hierarchie',
      'Klikatelnost a rozestup odkazů a tlačítek',
      'Blokovaný zoom a překážky ovládání',
      'Responzivita a chování na mobilu',
    ],
    why: 'Dobrý UX drží návštěvníky na webu déle, snižuje odchodovost a zvyšuje konverze. Signály chování uživatelů zároveň nepřímo ovlivňují SEO: když lidé rychle odcházejí, vyhledávač to vnímá jako nízkou relevanci.',
    faq: [
      { q: 'Ovlivňuje UX pozice ve vyhledávání?', a: 'Nepřímo. Lepší použitelnost zlepšuje signály chování (čas na stránce, prokliky), které souvisejí s vnímanou kvalitou webu.' },
      { q: 'Co nejvíc kazí UX?', a: 'Drobné písmo, nízký kontrast, pomalé načítání, vyskakovací okna a špatné ovládání na mobilu.' },
      { q: 'Je to to samé jako přístupnost?', a: 'Překrývá se, ale není totožné. Přístupnost cílí na lidi s postižením, UX na pohodlí všech uživatelů.' },
    ],
    related: ['kontrola-pristupnosti', 'test-mobilni-optimalizace', 'test-rychlosti-webu'], kb: 'ux',
  },
  {
    slug: 'kontrola-cookies-gdpr', toolId: 'cookies', inputType: 'url',
    metaTitle: 'Kontrola cookies a GDPR webu zdarma | SEO Tools',
    metaDesc: 'Zkontrolujte cookie lištu, zásady ochrany osobních údajů a sledovací skripty na webu. Ověřte soulad s GDPR. Zdarma, bez registrace.',
    h1: 'Kontrola cookies a GDPR',
    intro: 'Ověřte, zda váš web správně řeší cookies a soukromí: cookie lišta, zásady ochrany údajů a sledovací skripty.',
    what: [
      'Přítomnost a chování cookie lišty (consent)',
      'Odkaz na zásady ochrany osobních údajů',
      'Sledovací a marketingové skripty',
      'Cookies nastavené před souhlasem',
      'Soulad se zásadami GDPR / ePrivacy',
    ],
    why: 'Nesprávné nakládání s cookies může znamenat pokuty podle GDPR a poškozuje důvěru návštěvníků. Skripty, které sbírají data před souhlasem, jsou častým prohřeškem. Transparentní přístup k soukromí navíc posiluje důvěryhodnost značky.',
    faq: [
      { q: 'Musí mít web cookie lištu?', a: 'Pokud používá cookies nad rámec nezbytně nutných (analytika, marketing), musí podle GDPR/ePrivacy získat souhlas návštěvníka.' },
      { q: 'Smí se cookies nastavit před souhlasem?', a: 'Jen ty technicky nezbytné. Analytické a marketingové cookies až po uděleném souhlasu.' },
      { q: 'Souvisí GDPR se SEO?', a: 'Přímo ne, ale důvěra a transparentnost jsou součástí celkové kvality a reputace webu.' },
    ],
    related: ['kontrola-bezpecnostnich-hlavicek', 'kontrola-dns-ssl', 'detekce-technologii'], kb: 'cookies',
  },
  {
    slug: 'kontrola-dns-ssl', toolId: 'dns', inputType: 'url',
    metaTitle: 'Kontrola DNS a SSL certifikátu zdarma | SEO Tools',
    metaDesc: 'Online kontrola SSL certifikátu a DNS záznamů domény: platnost HTTPS, SPF, DMARC a MX. Zdarma, bez registrace.',
    h1: 'Kontrola DNS a SSL',
    intro: 'Zkontrolujte SSL certifikát a DNS záznamy své domény: platnost HTTPS i nastavení e-mailové ochrany (SPF, DMARC).',
    what: [
      'Platnost a vydavatel SSL certifikátu',
      'Funkčnost HTTPS a doba expirace',
      'DNS záznamy A, AAAA, MX',
      'E-mailová ochrana SPF a DMARC',
      'Základní zdraví domény',
    ],
    why: 'Platné HTTPS je faktor hodnocení i podmínka důvěry: prohlížeče u nezabezpečených webů zobrazují varování. Správné SPF/DMARC zase chrání vaši doménu před zneužitím k phishingu a zlepšují doručitelnost e-mailů.',
    faq: [
      { q: 'Je SSL/HTTPS důležité pro SEO?', a: 'Ano. HTTPS je potvrzený (byť slabý) faktor hodnocení a hlavně podmínka důvěry: prohlížeče HTTP weby označují jako nezabezpečené.' },
      { q: 'Co je SPF a DMARC?', a: 'DNS záznamy, které ověřují, kdo smí odesílat e-maily z vaší domény. Chrání před podvržením adresy a zlepšují doručitelnost.' },
      { q: 'Co když certifikát brzy vyprší?', a: 'Po vypršení přestane HTTPS fungovat a návštěvníci uvidí bezpečnostní varování. Certifikát je třeba včas obnovit.' },
    ],
    related: ['kontrola-bezpecnostnich-hlavicek', 'kontrola-presmerovani', 'detekce-technologii'], kb: 'https',
  },
  {
    slug: 'kontrola-mrtvych-odkazu', toolId: 'broken-links', inputType: 'url',
    metaTitle: 'Kontrola mrtvých odkazů na webu zdarma | SEO Tools',
    metaDesc: 'Najděte rozbité (404) odkazy na stránce, interní i externí. Zlepšete použitelnost a SEO. Zdarma, bez registrace.',
    h1: 'Kontrola mrtvých odkazů',
    intro: 'Najděte na své stránce rozbité odkazy (404), interní i externí, které kazí dojem a plýtvají crawl budgetem.',
    what: [
      'Všechny odkazy na stránce a jejich stavové kódy',
      'Rozbité odkazy (404) a chyby serveru (5xx)',
      'Interní vs externí odkazy',
      'Přesměrované odkazy (3xx)',
      'Souhrn problémů s doporučením',
    ],
    why: 'Mrtvé odkazy frustrují návštěvníky a signalizují vyhledávačům zanedbaný web. U interních odkazů navíc plýtvají crawl budgetem a tříští SEO sílu. Pravidelná kontrola udrží web zdravý a důvěryhodný.',
    faq: [
      { q: 'Škodí mrtvé odkazy SEO?', a: 'Nepřímo. Zhoršují uživatelský dojem a u interních odkazů tříští procházení a předávání hodnoty mezi stránkami.' },
      { q: 'Co s nalezenými 404?', a: 'Opravit cíl odkazu, nahradit ho funkčním, nebo odkaz odstranit. U smazaných stránek nastavit 301 na relevantní náhradu.' },
      { q: 'Kontrolovat i externí odkazy?', a: 'Ano. Odkazy na cizí weby mohou zmizet kdykoli; rozbité externí odkazy také zhoršují důvěryhodnost.' },
    ],
    related: ['kontrola-presmerovani', 'kontrola-sitemap', 'kontrola-robots-txt'], kb: 'links',
  },
  {
    slug: 'srovnani-webu', toolId: 'compare', inputType: 'compare',
    metaTitle: 'Srovnání dvou webů: SEO porovnání s konkurencí zdarma | SEO Tools',
    metaDesc: 'Porovnejte svůj web s konkurencí vedle sebe: SEO skóre, rychlost, obsah a technické parametry. Zdarma, bez registrace.',
    h1: 'Srovnání webů (vy vs konkurence)',
    intro: 'Postavte svůj web vedle konkurence a zjistěte, v čem zaostáváte a kde naopak vedete.',
    what: [
      'SEO skóre obou webů vedle sebe',
      'Rychlost a technické parametry',
      'Rozsah a struktura obsahu',
      'Meta tagy a strukturovaná data',
      'Konkrétní rozdíly a příležitosti',
    ],
    why: 'Konkurenční analýza ukáže, proč se konkurent umisťuje výš a co konkrétně zlepšit. Srovnání vedle sebe odhalí mezery, které byste jinak přehlédli: od rychlosti přes obsah až po technické SEO.',
    faq: [
      { q: 'K čemu je srovnání webů?', a: 'Ukáže rozdíly mezi vaším webem a konkurencí v klíčových SEO parametrech a pomůže určit priority zlepšení.' },
      { q: 'Které weby srovnávat?', a: 'Ideálně ty, které se umisťují na první stránce pro vaše klíčová slova: to jsou vaši reální konkurenti ve výsledcích.' },
      { q: 'Co s výsledky srovnání?', a: 'Zaměřte se na oblasti, kde nejvíc zaostáváte a které mají největší dopad: typicky rychlost, obsah a technické SEO.' },
    ],
    related: ['analyza-obsahu', 'test-rychlosti-webu', 'hromadna-kontrola-url'],
  },
  {
    slug: 'hromadna-kontrola-url', toolId: 'bulk', inputType: 'bulk',
    metaTitle: 'Hromadná kontrola URL: bulk SEO check zdarma | SEO Tools',
    metaDesc: 'Zkontrolujte více URL najednou: stavové kódy, title, meta a základní SEO parametry hromadně. Zdarma, bez registrace.',
    h1: 'Hromadná kontrola URL (bulk)',
    intro: 'Zkontrolujte více adres najednou a získejte rychlý přehled o stavu a základním SEO většího počtu stránek.',
    what: [
      'Stavové kódy (200, 301, 404…) pro každou URL',
      'Title a meta description jednotlivých stránek',
      'Indexovatelnost a základní SEO signály',
      'Rychlé odhalení problémových stránek',
      'Přehledná tabulka výsledků',
    ],
    why: 'U větších webů je nemožné kontrolovat stránky po jedné. Hromadná kontrola odhalí rozbité, přesměrované nebo špatně otagované stránky najednou a ušetří hodiny ruční práce při auditu nebo migraci.',
    faq: [
      { q: 'Kolik URL můžu zkontrolovat najednou?', a: 'Nástroj zpracuje seznam více adres v jednom běhu, ideální pro rychlý hromadný přehled stavu stránek.' },
      { q: 'Na co se hromadná kontrola hodí?', a: 'Na audity, migrace webu a pravidelnou kontrolu, zda někde nevznikly 404 nebo špatná přesměrování.' },
      { q: 'Co kontrolovat po migraci?', a: 'Hlavně stavové kódy a přesměrování: že staré URL správně (301) míří na nové a nikde nevznikla chyba 404.' },
    ],
    related: ['kontrola-mrtvych-odkazu', 'kontrola-presmerovani', 'srovnani-webu'],
  },
  {
    slug: 'geo-ai-optimalizace', toolId: 'geo', inputType: 'url',
    metaTitle: 'GEO analýza: optimalizace pro AI vyhledávače (ChatGPT, Perplexity) | SEO Tools',
    metaDesc: 'Zjistěte, jak je web připravený pro AI vyhledávače a generativní engine (ChatGPT, Gemini, Perplexity). Přístup AI botů, citovatelnost, llms.txt a E-E-A-T. Zdarma.',
    h1: 'GEO: optimalizace pro AI vyhledávače',
    intro: 'Generative Engine Optimization (GEO) je nové SEO pro éru AI. Zjistěte, jak je váš web připravený na to být citován v ChatGPT, Perplexity a Google AI.',
    what: [
      'Přístup AI botů (GPTBot, ClaudeBot, PerplexityBot…)',
      'Soubor llms.txt pro AI vyhledávače',
      'Citovatelnost: FAQ, statistiky, jasné odpovědi',
      'Signály E-E-A-T (zkušenost, expertíza, důvěryhodnost)',
      'Strukturovaná data a čerstvost obsahu',
    ],
    why: 'Stále víc lidí hledá přes ChatGPT, Perplexity nebo Google AI Overviews místo klasického vyhledávání. GEO rozhoduje, zda váš obsah AI najde, pochopí a ocituje, nebo zda doporučí konkurenci. Je to příležitost být první, protože většina webů na to ještě není připravená.',
    faq: [
      { q: 'Co je GEO (Generative Engine Optimization)?', a: 'Optimalizace obsahu tak, aby ho AI vyhledávače (ChatGPT, Perplexity, Gemini) snadno našly, pochopily a citovaly ve svých odpovědích.' },
      { q: 'Jak se liší GEO od SEO?', a: 'SEO cílí na pozice v klasickém vyhledávání, GEO na to, aby vás citovaly AI nástroje. Mnoho principů se překrývá, ale GEO klade větší důraz na jasné odpovědi, struktura a důvěryhodnost.' },
      { q: 'Co je llms.txt?', a: 'Soubor (obdoba robots.txt) v kořeni webu, který AI vyhledávačům srozumitelně shrnuje obsah a strukturu webu.' },
      { q: 'Jak povolit AI bity?', a: 'V robots.txt nastavit pravidla pro AI boty (GPTBot, ClaudeBot, PerplexityBot…) tak, aby směli web procházet, pokud chcete být v AI odpovědích.' },
    ],
    related: ['validace-strukturovanych-dat', 'analyza-obsahu', 'kontrola-robots-txt'], kb: 'geo',
  },
  {
    slug: 'lighthouse-audit', toolId: 'lighthouse', inputType: 'url',
    metaTitle: 'Lighthouse audit online zdarma: výkon, přístupnost, SEO | SEO Tools',
    metaDesc: 'Spusťte reálný Google Lighthouse audit přímo v prohlížeči: výkon, přístupnost, doporučené postupy, SEO a Core Web Vitals (LCP, CLS, TBT). Mobil i desktop. Zdarma.',
    h1: 'Lighthouse audit',
    intro: 'Zadejte web a získáte kompletní Lighthouse audit jako v Chrome DevTools: čtyři kategorie se skóre, Core Web Vitals a konkrétní doporučení s úsporou v milisekundách. Přepínání mezi mobilní a desktopovou verzí.',
    what: [
      'Skóre ve 4 kategoriích: Výkon, Přístupnost, Doporučené postupy, SEO',
      'Core Web Vitals: LCP, CLS, TBT, FCP, Speed Index, TTI',
      'Náhled stránky a filmstrip průběhu načítání',
      'Konkrétní příležitosti ke zrychlení s odhadem úspory',
      'Kompletní seznam všech auditů po kategoriích (mobil i desktop)',
    ],
    why: 'Lighthouse je standard od Googlu pro měření kvality webu. Skóre výkonu a Core Web Vitals přímo ovlivňují pozice ve vyhledávání i konverze: pomalý web ztrácí návštěvníky i umístění. Audit ukáže přesně, co stránku brzdí a kolik milisekund ušetříte konkrétní úpravou.',
    faq: [
      { q: 'Co je Google Lighthouse?', a: 'Open-source nástroj od Googlu (zabudovaný v Chrome DevTools), který auditovuje výkon, přístupnost, doporučené postupy a SEO webové stránky a dává skóre 0–100 v každé kategorii.' },
      { q: 'Jaký je rozdíl mezi Lighthouse a PageSpeed?', a: 'Lighthouse spouští reálný prohlížeč a měří skutečné Core Web Vitals a renderování. Náš nástroj PageSpeed dělá rychlou statickou heuristiku z HTML. Lighthouse je přesnější a podrobnější.' },
      { q: 'Proč se liší skóre mobil a desktop?', a: 'Mobilní audit simuluje pomalejší zařízení a síť, takže skóre výkonu bývá nižší než na desktopu. Google indexuje primárně mobilní verzi, proto je důležitější.' },
      { q: 'Co jsou Core Web Vitals?', a: 'Sada metrik Googlu pro uživatelský zážitek: LCP (rychlost načtení hlavního obsahu), CLS (vizuální stabilita) a INP/TBT (odezva na interakci). Jsou součástí hodnocení pro vyhledávání.' },
    ],
    related: ['test-rychlosti-webu', 'real-user-data-crux', 'kontrola-pristupnosti'],
  },
  {
    slug: 'real-user-data-crux', toolId: 'crux', inputType: 'url',
    metaTitle: 'Real User Data (CrUX): reálná Core Web Vitals od návštěvníků | SEO Tools',
    metaDesc: 'Chrome UX Report online: reálná data Core Web Vitals (LCP, INP, CLS) od skutečných návštěvníků webu za posledních 28 dní. Mobil i desktop. Zdarma.',
    h1: 'Real User Data (Chrome UX Report)',
    intro: 'Na rozdíl od laboratorního Lighthouse ukazuje CrUX, jak web zažívají skuteční návštěvníci v prohlížeči Chrome: terénní data Core Web Vitals (75. percentil) za posledních 28 dní.',
    what: [
      'Reálná Core Web Vitals: LCP, INP, CLS, FCP, TTFB',
      'Hodnota 75. percentilu (p75) skutečných uživatelů',
      'Rozložení zážitku: dobré / vyžaduje zlepšení / špatné',
      'Data za posledních 28 dní z prohlížeče Chrome',
      'Přepínání mezi mobilní a desktopovou zkušeností',
    ],
    why: 'Google používá při hodnocení webu právě terénní data z CrUX, ne laboratorní měření. Pokud máte dobré skóre v Lighthouse, ale špatná reálná data, vyhledávač počítá s tím horším. CrUX ukáže pravdu o tom, jak rychlý je web pro vaše skutečné návštěvníky.',
    faq: [
      { q: 'Co je Chrome UX Report (CrUX)?', a: 'Veřejná datová sada Googlu s reálnými metriky výkonu od skutečných uživatelů Chrome, kteří souhlasili se sdílením statistik. Je zdrojem terénních Core Web Vitals.' },
      { q: 'Proč nejsou pro můj web data?', a: 'CrUX zveřejňuje data jen pro weby s dostatečným počtem návštěvníků. Menší weby v datové sadě nejsou, pro ně použijte laboratorní Lighthouse.' },
      { q: 'Jaký je rozdíl mezi CrUX a Lighthouse?', a: 'Lighthouse je laboratorní test (jedno měření v simulovaném prostředí). CrUX jsou terénní data od mnoha reálných uživatelů za 28 dní. Google se rozhoduje podle CrUX.' },
      { q: 'Co znamená 75. percentil (p75)?', a: 'Hodnota, kterou má 75 % návštěvníků lepší nebo stejnou. Google ji používá jako reprezentativní: web je „dobrý", až když ho rychle zažívá většina uživatelů.' },
    ],
    related: ['lighthouse-audit', 'test-rychlosti-webu', 'analyza-obsahu'],
  },
  {
    slug: 'kontrola-safe-browsing', toolId: 'safe-browsing', inputType: 'url',
    metaTitle: 'Google Safe Browsing kontrola: je web bezpečný? | SEO Tools',
    metaDesc: 'Ověřte, zda Google neoznačil web za malware, phishing nebo nežádoucí software. Online kontrola přes Google Safe Browsing. Zdarma a bez registrace.',
    h1: 'Kontrola Safe Browsing',
    intro: 'Zadejte web a okamžitě zjistíte, zda ho Google neeviduje jako nebezpečný: kvůli malwaru, phishingu nebo nežádoucímu softwaru. Stejná databáze, kterou používá Chrome k varování uživatelů.',
    what: [
      'Detekce malwaru na webu',
      'Phishing a sociální inženýrství',
      'Nežádoucí a potenciálně škodlivý software',
      'Stav v databázi Google Safe Browsing',
    ],
    why: 'Když Google web označí za nebezpečný, Chrome i Firefox zobrazí výstražnou stránku a návštěvníci utečou. Web navíc může vypadnout z výsledků vyhledávání. Pravidelná kontrola odhalí napadení dřív, než poškodí návštěvníky i vaše pozice a reputaci.',
    faq: [
      { q: 'Co je Google Safe Browsing?', a: 'Služba Googlu, která udržuje seznam nebezpečných webů (malware, phishing). Používá ji Chrome, Firefox i Safari k varování uživatelů před návštěvou škodlivé stránky.' },
      { q: 'Co když je web označen jako nebezpečný?', a: 'Web byl pravděpodobně napadený. Je třeba najít a odstranit škodlivý kód, a poté požádat o nové prověření v Google Search Console (Bezpečnostní problémy).' },
      { q: 'Ovlivní napadení weby SEO?', a: 'Ano, výrazně. Označený web ztrácí důvěru, Chrome odrazuje návštěvníky výstrahou a Google může snížit pozice nebo web dočasně vyřadit.' },
    ],
    related: ['kontrola-bezpecnostnich-hlavicek', 'kontrola-dns-ssl', 'kontrola-cookies-gdpr'],
  },
  {
    slug: 'kontrola-indexace-google', toolId: 'indexation', inputType: 'url',
    metaTitle: 'Kontrola indexovatelnosti: smí Google zaindexovat stránku? | SEO Tools',
    metaDesc: 'Zjistěte, jestli vaši stránku smí vyhledávače zaindexovat: noindex, robots.txt, canonical i sitemapa. Plus odkaz na site: dotaz do Googlu. Zdarma.',
    h1: 'Indexovatelnost stránky',
    intro: 'Zadejte URL a nástroj zkontroluje, zda stránku smí vyhledávače zaindexovat: meta robots noindex, blokaci v robots.txt, canonical i přítomnost v sitemapě. Reálný počet zaindexovaných stránek pak ověříte jedním klikem přes site: dotaz v Googlu.',
    what: [
      'Verdikt, zda je stránka indexovatelná, nebo ne',
      'Kontrola noindex (meta robots i X-Robots-Tag), robots.txt a canonical',
      'Ověření, jestli je URL v sitemap.xml + odkaz na site: dotaz do Googlu',
    ],
    why: 'Stránka s noindexem, blokací v robots.txt nebo canonicalem mířícím jinam se do indexu nedostane, a tím pádem ani do výsledků vyhledávání. Často jde o nechtěnou chybu (zapomenutý noindex ze staging verze, špatné pravidlo v robots.txt). Kontrola indexovatelnosti tyhle blokery odhalí dřív, než vás stojí návštěvnost.',
    faq: [
      { q: 'Co přesně nástroj kontroluje?', a: 'Meta tag robots a hlavičku X-Robots-Tag (noindex), pravidla v robots.txt pro Googlebota, canonical tag (zda neukazuje na jinou URL) a přítomnost stránky v sitemap.xml. Z toho složí jasný verdikt, jestli stránka smí být zaindexována.' },
      { q: 'Zjistím i počet už zaindexovaných stránek?', a: 'Přesný počet udává jen Google sám, proto nástroj nabízí tlačítko, které otevře dotaz „site:vasedomena.cz" přímo v Googlu, kde uvidíte aktuální číslo. Google pro tento dotaz už neposkytuje veřejné API.' },
      { q: 'Proč stránka není v indexu, i když je indexovatelná?', a: 'Indexovatelnost je nutná podmínka, ne záruka. Stránka může být příliš nová, bez zpětných odkazů, duplicitní, nebo ji Google zatím neprošel. Pro přesný stav indexace použijte Google Search Console (Stránky).' },
    ],
    related: ['kontrola-sitemap', 'kontrola-robots-txt', 'kontrola-presmerovani'],
  },
  {
    slug: 'generator-llms-txt', toolId: 'llms-txt', inputType: 'url',
    metaTitle: 'Generátor llms.txt: soubor pro AI vyhledávače zdarma | SEO Tools',
    metaDesc: 'Vygenerujte soubor llms.txt pro ChatGPT, Perplexity a další AI vyhledávače. Z webu vytáhne název, popis a hlavní stránky. Zdarma, ke stažení.',
    h1: 'Generátor llms.txt',
    intro: 'Zadejte web a nástroj vygeneruje hotový soubor llms.txt: standard, kterým weby napovídají AI vyhledávačům (ChatGPT, Perplexity, Claude), co je na webu důležité. Z homepage a sitemapy vytáhne název, popis a hlavní stránky a poskládá je do správného formátu ke stažení.',
    what: [
      'Hotový soubor llms.txt ke stažení',
      'Název a popis webu + hlavní stránky s odkazy',
      'Doplnění dalších stránek ze sitemapy',
    ],
    why: 'AI vyhledávače a asistenti čím dál víc rozhodují, odkud berou odpovědi. Soubor llms.txt (obdoba robots.txt, ale pro AI) jim dává čistý, strukturovaný přehled o webu, bez nutnosti procházet HTML, reklamy a navigaci. Weby s llms.txt mají větší šanci být správně pochopené a citované v AI odpovědích.',
    faq: [
      { q: 'Co je llms.txt?', a: 'Je to textový soubor v kořeni webu (např. vasedomena.cz/llms.txt), který srozumitelně popisuje obsah webu pro velké jazykové modely a AI vyhledávače. Vznikl jako standard llmstxt.org a pomáhá AI rychle pochopit, co je na webu podstatné.' },
      { q: 'Kam soubor nahrát?', a: 'Stáhněte vygenerovaný soubor a nahrajte ho do kořenového adresáře webu tak, aby byl dostupný na adrese https://vasedomena.cz/llms.txt, stejně jako robots.txt.' },
      { q: 'Musím něco platit nebo mít AI klíč?', a: 'Ne. Generátor pracuje čistě z veřejně dostupného obsahu vašeho webu, je zdarma a nevyžaduje žádný API klíč.' },
    ],
    related: ['kontrola-robots-txt', 'kontrola-sitemap', 'kontrola-indexace-google'],
  },
]

export function getToolPage(slug: string): ToolPage | undefined {
  return TOOL_PAGES.find((t) => t.slug === slug)
}

export const TOOL_PAGE_SLUGS = TOOL_PAGES.map((t) => t.slug)

// --- Lokalizovaný název nástroje (h1) ---
// Centralizováno tady, ať výpisy (related/guide) neberou český h1 z předlohy
// (opakující se chyba). Vždy použij toolH1(slug, lang).
import tpEn from './toolPages.en.json'
import tpDe from './toolPages.de.json'
import tpPl from './toolPages.pl.json'
import tpSk from './toolPages.sk.json'
import tpIt from './toolPages.it.json'
import tpHu from './toolPages.hu.json'
import tpUk from './toolPages.uk.json'
import tpEs from './toolPages.es.json'
import tpFr from './toolPages.fr.json'
import tpNl from './toolPages.nl.json'
import tpJa from './toolPages.ja.json'

const TP_TRANS: Record<string, any[]> = { en: tpEn, de: tpDe, pl: tpPl, sk: tpSk, it: tpIt, hu: tpHu, uk: tpUk, es: tpEs, fr: tpFr, nl: tpNl, ja: tpJa }

/** Lokalizovaný H1 (název) nástroje pro daný jazyk; fallback en → česká předloha. */
export function toolH1(slug: string, lang: string): string {
  const base = TOOL_PAGES.find((t) => t.slug === slug)
  const tr = (TP_TRANS[lang] || (lang !== 'cs' && TP_TRANS.en) || []).find((x: any) => x.slug === slug)
  return (tr?.h1 as string) || base?.h1 || slug
}
